import React from 'react';
import {
  Input,
  Divider,
  Tag,
  Button,
  Tooltip,
  Typography,
} from 'antd';
import {
  SaveOutlined,
} from '@ant-design/icons';
import { Customer, Vehicle } from '../../types';
import PartsCatalogButton from '../PartsCatalogButton';
import FeesEditor from '../Shared/FeesEditor';
import { TaxExemptionToggle } from '../Shared/TaxExemptionToggle';
import SendDialog from '../Shared/SendDialog';

const { Text } = Typography;

export interface EstimateFormFieldsProps {
  isMobile: boolean;
  loading: boolean;
  isFormValid: boolean;
  autoSaveStatus: 'idle' | 'saving' | 'saved';
  subtotal: number;
  taxAmount: number;
  total: number;
  taxRateInput: string;
  onTaxRateChange: (value: string) => void;
  onSave: (status: 'draft' | 'sent') => void;
  /** Persist current edits in place (no navigation) and resolve to the saved
   *  estimate id. The Send menu calls this first so one press saves-then-sends. */
  onSaveBeforeSend?: () => Promise<number | undefined>;
  selectedVehicle: Vehicle | null;
  saveDisabledReason?: string;
  /** Resolved estimate ID for send/print actions. Undefined when draft not yet persisted. */
  estimateId?: number;
  /** Selected customer (for tax exemption toggle). */
  selectedCustomer?: Customer | null;
  /** Precomputed customer-facing estimate share URL, passed to the Send menu. */
  shareUrl?: string;
}

/**
 * Slim sticky action bar: totals + Save/Send only.
 *
 * Valid Until / Customer Notes / Internal Notes moved to EstimateNotesSection (in the
 * scrollable body) so this footer stays compact and the Service Items table keeps its
 * space. No Form.Item lives here anymore, so there is no <Form> wrapper.
 */
export const EstimateFormFields: React.FC<EstimateFormFieldsProps> = ({
  isMobile,
  loading,
  isFormValid,
  autoSaveStatus,
  subtotal,
  taxAmount,
  total,
  taxRateInput,
  onTaxRateChange,
  onSave,
  onSaveBeforeSend,
  selectedVehicle,
  saveDisabledReason,
  estimateId,
  selectedCustomer,
  shareUrl,
}) => {
  const inputStyle = { fontSize: 16 };

  if (isMobile) {
    return (
      <div style={{
        borderTop: '1px solid #e5e7eb',
        backgroundColor: '#ffffff',
        padding: '12px',
        boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)',
        paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
        flexShrink: 0,
        zIndex: 20,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Totals row */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f9fafb',
            padding: '10px 12px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontSize: 12, color: '#6b7280' }}>Tax</Text>
              <Input
                size="small"
                value={taxRateInput}
                onChange={(e) => onTaxRateChange(e.target.value)}
                disabled={loading}
                style={{ width: 52, textAlign: 'center', ...inputStyle, height: 28 }}
                suffix={<span style={{ fontSize: 11, color: '#6b7280' }}>%</span>}
              />
            </div>
            <div style={{ textAlign: 'right' }}>
              <Text style={{ fontSize: 11, color: '#6b7280', display: 'block' }}>
                Subtotal: ${subtotal.toFixed(2)} + Tax: ${taxAmount.toFixed(2)}
              </Text>
              <Text strong style={{ fontSize: 20, color: '#10b981' }}>${total.toFixed(2)}</Text>
            </div>
          </div>
          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={() => onSave('draft')}
              loading={loading}
              disabled={loading}
              size="large"
              style={{ flex: 2, height: 48, fontWeight: 600, fontSize: 15 }}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
            <SendDialog
              entityType="estimates"
              entityId={estimateId}
              shareUrl={shareUrl}
              onSaveBeforeSend={onSaveBeforeSend}
              vehicleHasVin={!!selectedVehicle?.vin}
              vehicleHasMileage={!!selectedVehicle?.current_mileage}
              disabled={loading || !isFormValid}
            />
          </div>
        </div>
      </div>
    );
  }

  // Desktop slim action bar: totals (left) + actions (right)
  return (
    <div style={{
      borderTop: '1px solid #e5e7eb',
      backgroundColor: '#ffffff',
      padding: '12px 20px',
      boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)',
      flexShrink: 0,
      zIndex: 20,
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      flexWrap: 'wrap',
    }}>
      {/* Totals */}
      <div style={{
        backgroundColor: '#f9fafb',
        padding: '8px 12px',
        borderRadius: '6px',
        border: '1px solid #e5e7eb',
        flex: '1 1 320px',
        minWidth: 280,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <Text style={{ fontSize: '11px', color: '#6b7280' }}>Subtotal</Text>
          <Text style={{ fontSize: '11px', fontWeight: 500 }}>${subtotal.toFixed(2)}</Text>
        </div>
        {estimateId && (
          <div style={{ marginBottom: '4px' }}>
            <FeesEditor entityType="estimates" entityId={estimateId} editable />
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Text style={{ fontSize: '11px', color: '#6b7280' }}>Tax</Text>
            <Input
              size="small"
              value={taxRateInput}
              onChange={(e) => onTaxRateChange(e.target.value)}
              disabled={loading}
              style={{ width: '60px', textAlign: 'center', fontSize: '11px', height: '24px', lineHeight: '1', padding: '4px 24px 4px 8px', display: 'flex', alignItems: 'center' }}
              suffix={<span style={{ fontSize: '11px', color: '#6b7280' }}>%</span>}
            />
            {/* is_tax_exempt arrives from SQLite as 0/1, and a bare `0 &&` renders
                a literal "0" next to the tax box. Coerce before the guard. */}
            {Boolean(selectedCustomer && (selectedCustomer as any).is_tax_exempt) && (
              <TaxExemptionToggle
                customer={selectedCustomer as any}
                onTaxRateChange={(rate) => onTaxRateChange(String(rate))}
              />
            )}
          </div>
          <Text style={{ fontSize: '11px', fontWeight: 500 }}>${taxAmount.toFixed(2)}</Text>
        </div>
        <Divider style={{ margin: '6px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong style={{ fontSize: '13px' }}>Total</Text>
          <Text strong style={{ fontSize: '16px', color: '#10b981' }}>${total.toFixed(2)}</Text>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center', marginLeft: 'auto' }}>
        {autoSaveStatus === 'saving' && (
          <Tag color="processing" style={{ marginRight: 8 }}>Auto-saving...</Tag>
        )}
        {autoSaveStatus === 'saved' && (
          <Tag color="success" style={{ marginRight: 8 }}>Auto-saved</Tag>
        )}
        <PartsCatalogButton
          vehicle={selectedVehicle || undefined}
          size="middle"
          showText={false}
        />
        <Tooltip title={saveDisabledReason}>
          <Button
            size="middle"
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => onSave('draft')}
            loading={loading}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Tooltip>
        <SendDialog
          entityType="estimates"
          entityId={estimateId}
          shareUrl={shareUrl}
          onSaveBeforeSend={onSaveBeforeSend}
          vehicleHasVin={!!selectedVehicle?.vin}
          vehicleHasMileage={!!selectedVehicle?.current_mileage}
          disabled={loading || !isFormValid}
        />
      </div>
    </div>
  );
};
